import {
  Box,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Plus, PencilSimple, TrashSimple } from '@phosphor-icons/react';
import { useDispatch, useSelector } from 'react-redux';
import {
  addParameter,
  updateParameter,
  removeParameter,
  addFormField,
  updateFormField,
  removeFormField,
  FormField,
  FormFieldType,
  Parameter,
  setFunctionName,
  setFunctionDescription,
  setFormTitle,
  setFormDescription,
  setSubmitButtonText,
  setSuccessMessage,
} from '../../../utils/redux/slices/webformSlice';
import { RootState } from '../../../utils/redux';
import DeleteModal from '../../modals/DeleteModal';
import { useState, useEffect } from 'react';
import theme from '../../../theme';

const WebFormBuilder = () => {
  const dispatch = useDispatch();
  const {
    functionName,
    functionDescription,
    formConfig,
    formFields,
    parameters,
  } = useSelector((state: RootState) => state.webform);

  // Parameters state
  const [isAddingParam, setIsAddingParam] = useState(false);
  const [isEditingParam, setIsEditingParam] = useState(false);
  const [editingParamIndex, setEditingParamIndex] = useState<number>(-1);
  const [newParam, setNewParam] = useState<Parameter>({
    name: '',
    type: 'string',
    description: '',
    required: false,
  });

  // Form Fields state
  const [isAddingFormField, setIsAddingFormField] = useState(false);
  const [isEditingFormField, setIsEditingFormField] = useState(false);
  const [editingFieldIndex, setEditingFieldIndex] = useState<number>(-1);
  const [newFormField, setNewFormField] = useState<FormField>({
    name: '',
    label: '',
    type: 'text',
    required: false,
    placeholder: '',
    options: [],
  });

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingFormFieldIndex, setDeletingFormFieldIndex] =
    useState<number>(-1);
  const [isDeletingFormField, setIsDeletingFormField] = useState(false);
  const [deletingParamIndex, setDeletingParamIndex] = useState<number>(-1);
  const [isDeletingParam, setIsDeletingParam] = useState(false);
  const [deleteModalType, setDeleteModalType] = useState<
    'parameter' | 'formField'
  >('formField');

  // Effect to generate form fields from parameters
  useEffect(() => {
    // Sync parameters with form fields
    parameters.forEach(param => {
      const existingField = formFields.find(field => field.name === param.name);
      if (!existingField) {
        let fieldType: FormFieldType = 'text';
        switch (param.type) {
          case 'number':
            fieldType = 'number';
            break;
          case 'boolean':
            fieldType = 'checkbox';
            break;
          default:
            fieldType = 'text';
        }

        // Create a new form field based on the parameter
        const newField: FormField = {
          name: param.name,
          label: param.name.charAt(0).toUpperCase() + param.name.slice(1),
          type: fieldType,
          required: param.required,
          placeholder: param.description,
          options: fieldType === 'checkbox' ? [''] : undefined,
        };

        dispatch(addFormField(newField));
      }
    });
  }, [parameters, formFields, dispatch]);

  // Parameter functions
  const handleAddParameter = () => {
    setIsAddingParam(true);
    setIsEditingParam(false);
    setNewParam({
      name: '',
      type: 'string',
      description: '',
      required: false,
    });
  };

  const handleSaveParameter = () => {
    if (isEditingParam && editingParamIndex >= 0) {
      // Update existing parameter using Redux
      dispatch(
        updateParameter({
          index: editingParamIndex,
          parameter: { ...newParam },
        })
      );
      setEditingParamIndex(-1);
      setIsEditingParam(false);
    } else {
      // Add new parameter using Redux
      dispatch(addParameter({ ...newParam }));
      setIsAddingParam(false);
    }

    // Reset form state
    setNewParam({
      name: '',
      type: 'string',
      description: '',
      required: false,
    });
  };

  const handleCancelParameter = () => {
    setIsAddingParam(false);
    setIsEditingParam(false);
    setEditingParamIndex(-1);
    setNewParam({
      name: '',
      type: 'string',
      description: '',
      required: false,
    });
  };

  const handleEditParameter = (index: number) => {
    if (index >= 0 && index < parameters.length) {
      const paramToEdit = parameters[index];
      setNewParam({
        name: paramToEdit.name,
        type: paramToEdit.type,
        description: paramToEdit.description,
        required: paramToEdit.required,
      });
      setEditingParamIndex(index);
      setIsEditingParam(true);
      setIsAddingParam(false);
    }
  };

  const handleDeleteParameter = (index: number) => {
    setDeletingParamIndex(index);
    setDeleteModalType('parameter');
    setDeleteModalOpen(true);
  };

  const confirmDeleteParameter = () => {
    setIsDeletingParam(true);
    if (deletingParamIndex >= 0) {
      // Use Redux to remove the parameter
      dispatch(removeParameter(deletingParamIndex));
      setDeleteModalOpen(false);
      setDeletingParamIndex(-1);
    }
    setIsDeletingParam(false);
  };

  // Form Field functions
  const handleAddFormField = () => {
    setIsAddingFormField(true);
    setIsEditingFormField(false);
    // Reset form field
    setNewFormField({
      name: '',
      label: '',
      type: 'text',
      required: false,
      placeholder: '',
      options: [],
    });
  };

  const handleDeleteFormField = (index: number) => {
    setDeletingFormFieldIndex(index);
    setDeleteModalType('formField');
    setDeleteModalOpen(true);
  };

  const confirmDeleteFormField = () => {
    setIsDeletingFormField(true);
    if (deletingFormFieldIndex >= 0) {
      dispatch(removeFormField(deletingFormFieldIndex));
      setIsDeletingFormField(false);
      setDeleteModalOpen(false);
      setDeletingFormFieldIndex(-1);
    }
  };

  const handleCancelFormField = () => {
    setIsAddingFormField(false);
    setIsEditingFormField(false);
    setEditingFieldIndex(-1);
    setNewFormField({
      name: '',
      label: '',
      type: 'text',
      required: false,
      placeholder: '',
      options: [],
    });
  };

  const handleSaveFormField = () => {
    if (isEditingFormField && editingFieldIndex >= 0) {
      // Update existing field
      dispatch(
        updateFormField({
          index: editingFieldIndex,
          field: { ...newFormField },
        })
      );
      setEditingFieldIndex(-1);
      setIsEditingFormField(false);
    } else {
      // Add new field
      dispatch(addFormField({ ...newFormField }));
      setIsAddingFormField(false);
    }

    // Reset form state
    setNewFormField({
      name: '',
      label: '',
      type: 'text',
      required: false,
      placeholder: '',
      options: [],
    });
  };

  const handleEditFormField = (index: number) => {
    // Load field data into the form
    if (index >= 0 && index < formFields.length) {
      const fieldToEdit = formFields[index];
      setNewFormField({
        name: fieldToEdit.name,
        label: fieldToEdit.label,
        type: fieldToEdit.type,
        required: fieldToEdit.required,
        placeholder: fieldToEdit.placeholder || '',
        options: fieldToEdit.options,
      });
      setEditingFieldIndex(index);
      setIsEditingFormField(true);
      setIsAddingFormField(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        backgroundColor: '#FFFFFF',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '16px',
          width: '589px',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            width: '114px',
            fontWeight: 400,
            fontSize: '12px',
            color: '#6B6F80',
          }}
        >
          Function Name
        </Typography>
        <TextField
          fullWidth
          value={functionName}
          onChange={e => dispatch(setFunctionName(e.target.value))}
          placeholder="Enter the name of the function"
          sx={{
            width: '459px',
            height: '40px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '6px',
              backgroundColor: '#FFFFFF',
              '& fieldset': {
                borderWidth: '1px',
                borderColor: '#DCE0E7',
                '&:hover': {
                  borderColor: '#DCE0E7',
                },
              },
            },
            '& .MuiOutlinedInput-input': {
              padding: '8px 12px',
              gap: '10px',
              backgroundColor: '#FFFFFF',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '100%',
              letterSpacing: '-1%',
              color: '#302B3F',
              '&::placeholder': {
                color: '#6B6F80',
              },
            },
          }}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: '16px',
          width: '589px',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            width: '114px',
            fontWeight: 400,
            fontSize: '12px',
            color: '#6B6F80',
            textWrap: 'nowrap',
          }}
        >
          Function Description
        </Typography>
        <TextField
          fullWidth
          value={functionDescription}
          onChange={e => dispatch(setFunctionDescription(e.target.value))}
          placeholder="Enter the description of the function"
          sx={{
            width: '459px',
            height: '40px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '6px',
              backgroundColor: '#FFFFFF',
              '& fieldset': {
                borderWidth: '1px',
                borderColor: '#DCE0E7',
                '&:hover': {
                  borderColor: '#DCE0E7',
                },
              },
            },
            '& .MuiOutlinedInput-input': {
              padding: '8px 12px',
              gap: '10px',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '100%',
              letterSpacing: '-1%',
              color: '#302B3F',
              backgroundColor: '#FFFFFF',
              '&::placeholder': {
                color: '#6B6F80',
              },
            },
          }}
        />
      </Box>

      {/* Parameters Section */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '14px',
                color: '#302B3F',
              }}
            >
              Parameters
            </Typography>
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: '12px',
                color: '#6B6F80',
              }}
            >
              Define the parameters your function accepts
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<Plus size={16} />}
            onClick={handleAddParameter}
            disabled={isAddingParam}
            sx={{
              fontSize: 14,
              width: 'fit-content',
              height: '34px',
              fontWeight: 500,
              borderRadius: '6px',
              textTransform: 'none',
              boxShadow: 'none',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            Add Parameter
          </Button>
        </Box>

        {isAddingParam || isEditingParam ? (
          <Box
            sx={{
              backgroundColor: '#F8F8F8',
              borderRadius: '6px',
              padding: '16px',
              width: '100%',
              border: '1px solid #DCE0E7',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 2,
                '& > div': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  height: '40px',
                },
                '& .MuiTypography-root': {
                  width: '114px',
                  fontWeight: 400,
                  fontSize: '12px',
                  color: '#6B6F80',
                },
              }}
            >
              {/* Name & Type field */}
              <Box>
                <Typography>Name & Type</Typography>
                <TextField
                  value={newParam.name}
                  onChange={e =>
                    setNewParam({ ...newParam, name: e.target.value })
                  }
                  placeholder="name"
                  sx={{
                    width: '357px',
                    height: '40px',
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      borderRadius: '6px',
                      backgroundColor: '#FFFFFF',
                      '& fieldset': {
                        borderWidth: '1px',
                        borderColor: '#DCE0E7',
                        '&:hover': {
                          borderColor: '#DCE0E7',
                        },
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      padding: '8px 12px',
                      fontSize: '14px',
                      color: '#302B3F',
                      backgroundColor: '#FFFFFF',
                      '&::placeholder': {
                        color: '#6B6F80',
                      },
                    },
                  }}
                />
                <Select
                  value={newParam.type}
                  onChange={e =>
                    setNewParam({
                      ...newParam,
                      type: e.target.value as
                        | 'string'
                        | 'number'
                        | 'boolean'
                        | 'object'
                        | 'array',
                    })
                  }
                  sx={{
                    width: '100px',
                    height: '40px',
                    border: '1px solid #DCE0E7',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    boxShadow: 'none',
                    background: '#F8F9FC',
                    '.MuiOutlinedInput-notchedOutline': { border: 'none' },
                    '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
                      {
                        border: 'none',
                      },
                    '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                      {
                        border: 'none',
                      },
                    '& .MuiSelect-select': {
                      display: 'flex',
                      padding: '8px 12px',
                      paddingRight: '26px',
                      alignItems: 'center',
                      background: '#F8F9FC',
                      fontSize: '14px',
                      fontWeight: 400,
                      lineHeight: 'normal',
                      letterSpacing: '-0.14px',
                      color: theme.palette.lightGray.text,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                  }}
                >
                  <MenuItem value="string">string</MenuItem>
                  <MenuItem value="number">number</MenuItem>
                  <MenuItem value="boolean">boolean</MenuItem>
                  <MenuItem value="object">object</MenuItem>
                  <MenuItem value="array">array</MenuItem>
                </Select>
              </Box>

              {/* Description field */}
              <Box>
                <Typography>Description</Typography>
                <TextField
                  value={newParam.description}
                  onChange={e =>
                    setNewParam({ ...newParam, description: e.target.value })
                  }
                  placeholder="Parameter description"
                  fullWidth
                  rows={1}
                  sx={{
                    width: '459px',
                    height: '40px',
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      borderRadius: '6px',
                      backgroundColor: '#FFFFFF',
                      '& fieldset': {
                        borderWidth: '1px',
                        borderColor: '#DCE0E7',
                        '&:hover': {
                          borderColor: '#DCE0E7',
                        },
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      padding: '8px 12px',
                      fontSize: '14px',
                      color: '#302B3F',
                      backgroundColor: '#FFFFFF',
                      '&::placeholder': {
                        color: '#6B6F80',
                      },
                    },
                  }}
                />
              </Box>

              {/* Required checkbox */}
              <Box>
                <Typography>Required</Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newParam.required}
                      onChange={e =>
                        setNewParam({ ...newParam, required: e.target.checked })
                      }
                      sx={{
                        marginLeft: '8px',
                        width: '20px',
                        height: '20px',
                        color: 'transparent',
                        '&.Mui-checked': {
                          color: '#6366F1',
                        },
                        '& .MuiSvgIcon-root': {
                          borderRadius: '6px',
                          border: '1px solid #DCE0E7',
                          backgroundColor: '#FFFFFF',
                          width: '20px',
                          height: '20px',
                          '&:hover': {
                            borderColor: '#6366F1',
                          },
                        },
                      }}
                    />
                  }
                  label=""
                  sx={{
                    marginLeft: '-11px',
                    '& .MuiFormControlLabel-label': {
                      display: 'none',
                    },
                  }}
                />
              </Box>

              {/* Cancel & Save Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 2,
                  marginTop: 1,
                  height: 'auto !important', // Override the previous height
                }}
              >
                <Button
                  onClick={handleCancelParameter}
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                    textTransform: 'none',
                    color: '#6B6F80',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: '#302B3F',
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveParameter}
                  disabled={!newParam.name}
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                    textTransform: 'none',
                    boxShadow: 'none',
                    backgroundColor: '#6366F1',
                    borderRadius: '6px',
                    '&:hover': {
                      backgroundColor: '#4F46E5',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#E4E7EC',
                      color: '#9DA4AE',
                    },
                  }}
                >
                  {isEditingParam ? 'Update Parameter' : 'Save Parameter'}
                </Button>
              </Box>
            </Box>
          </Box>
        ) : parameters.length > 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              backgroundColor: '#F8F8F8',
              padding: '12px 16px',
              borderRadius: '6px',
              border: '1px solid #DCE0E7',
            }}
          >
            {parameters.map((param: Parameter, index: number) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  height: '40px',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 16px',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '8px',
                  border: '1px solid #DCE0E7',
                }}
              >
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: '12px',
                      color: '#6B6F80',
                    }}
                  >
                    {param.type}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: '14px',
                        color: '#302B3F',
                      }}
                    >
                      {param.name}
                    </Typography>
                    {param.required && (
                      <Tooltip title="Required">
                        <Typography
                          sx={{
                            color: '#EF4444',
                            fontSize: '14px',
                            marginLeft: '2px',
                          }}
                        >
                          *
                        </Typography>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton onClick={() => handleEditParameter(index)}>
                    <PencilSimple size={16} />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteParameter(index)}>
                    <TrashSimple size={16} weight="fill" color="#E74C3C" />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                backgroundColor: '#F8F8F8',
                borderRadius: '6px',
                padding: '16px',
                width: '100%',
                textAlign: 'center',
                color: '#6B6F80',
                fontSize: '12px',
                fontWeight: 400,
              }}
            >
              No parameters added. Click "Add Parameter" to add your first
              parameter
            </Box>
          </Box>
        )}
      </Box>

      {/* Form Configuration Section */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '14px',
              color: '#302B3F',
            }}
          >
            Form Configuration
          </Typography>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: '12px',
              color: '#6B6F80',
            }}
          >
            Configure API calling for your function
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            backgroundColor: '#F8F8F8',
            borderRadius: '6px',
            padding: '16px 8px',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: '16px',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{
                width: '114px',
                fontWeight: 400,
                fontSize: '12px',
                color: '#6B6F80',
              }}
            >
              Form Title
            </Typography>
            <TextField
              fullWidth
              value={formConfig.formTitle}
              onChange={e => dispatch(setFormTitle(e.target.value))}
              placeholder="Schedule an Appointment"
              sx={{
                width: '459px',
                height: '40px',
                '& .MuiOutlinedInput-root': {
                  height: '40px',
                  borderRadius: '6px',
                  backgroundColor: '#FFFFFF',
                  '& fieldset': {
                    borderWidth: '1px',
                    borderColor: '#DCE0E7',
                    '&:hover': {
                      borderColor: '#DCE0E7',
                    },
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '8px 12px',
                  fontSize: '14px',
                  color: '#302B3F',
                  backgroundColor: '#FFFFFF',
                  '&::placeholder': {
                    color: '#6B6F80',
                  },
                },
              }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: '16px',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{
                width: '114px',
                fontWeight: 400,
                fontSize: '12px',
                color: '#6B6F80',
              }}
            >
              Form Description
            </Typography>
            <TextField
              fullWidth
              value={formConfig.formDescription}
              onChange={e => dispatch(setFormDescription(e.target.value))}
              placeholder="Please fill in the form below to schedule your appointment with us."
              sx={{
                width: '459px',
                height: '40px',
                '& .MuiOutlinedInput-root': {
                  height: '40px',
                  borderRadius: '6px',
                  backgroundColor: '#FFFFFF',
                  '& fieldset': {
                    borderWidth: '1px',
                    borderColor: '#DCE0E7',
                    '&:hover': {
                      borderColor: '#DCE0E7',
                    },
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '8px 12px',
                  fontSize: '14px',
                  color: '#302B3F',
                  backgroundColor: '#FFFFFF',
                  '&::placeholder': {
                    color: '#6B6F80',
                  },
                },
              }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: '16px',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{
                width: '114px',
                fontWeight: 400,
                fontSize: '12px',
                color: '#6B6F80',
                textWrap: 'nowrap',
              }}
            >
              Submit Button Text
            </Typography>
            <TextField
              fullWidth
              value={formConfig.submitButtonText}
              onChange={e => dispatch(setSubmitButtonText(e.target.value))}
              placeholder="Book Appointment"
              sx={{
                width: '459px',
                height: '40px',
                '& .MuiOutlinedInput-root': {
                  height: '40px',
                  borderRadius: '6px',
                  backgroundColor: '#FFFFFF',
                  '& fieldset': {
                    borderWidth: '1px',
                    borderColor: '#DCE0E7',
                    '&:hover': {
                      borderColor: '#DCE0E7',
                    },
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '8px 12px',
                  fontSize: '14px',
                  color: '#302B3F',
                  backgroundColor: '#FFFFFF',
                  '&::placeholder': {
                    color: '#6B6F80',
                  },
                },
              }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: '16px',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{
                width: '114px',
                fontWeight: 400,
                fontSize: '12px',
                color: '#6B6F80',
                textWrap: 'nowrap',
              }}
            >
              Success Message
            </Typography>
            <TextField
              fullWidth
              value={formConfig.successMessage}
              onChange={e => dispatch(setSuccessMessage(e.target.value))}
              placeholder="Your appointment request has been submitted! We'll contact you to confirm the details."
              sx={{
                width: '459px',
                height: '40px',
                '& .MuiOutlinedInput-root': {
                  height: '40px',
                  borderRadius: '6px',
                  backgroundColor: '#FFFFFF',
                  '& fieldset': {
                    borderWidth: '1px',
                    borderColor: '#DCE0E7',
                    '&:hover': {
                      borderColor: '#DCE0E7',
                    },
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '8px 12px',
                  fontSize: '14px',
                  color: '#302B3F',
                  backgroundColor: '#FFFFFF',
                  '&::placeholder': {
                    color: '#6B6F80',
                  },
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Form Fields Section */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '14px',
                color: '#302B3F',
              }}
            >
              Form Fields
            </Typography>
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: '12px',
                color: '#6B6F80',
              }}
            >
              Define the parameters your function accepts
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<Plus size={16} />}
            onClick={handleAddFormField}
            disabled={isAddingFormField}
            sx={{
              fontSize: 14,
              width: 'fit-content',
              height: '34px',
              fontWeight: 500,
              borderRadius: '6px',
              textTransform: 'none',
              boxShadow: 'none',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            Add Field
          </Button>
        </Box>

        {/* Rest of the form fields section remains the same */}
        {isAddingFormField || isEditingFormField ? (
          <Box
            sx={{
              backgroundColor: '#F8F8F8',
              borderRadius: '6px',
              padding: '16px',
              width: '100%',
              border: '1px solid #DCE0E7',
            }}
          >
            {/* Original form field editor */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 2,
                '& > div': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  height: '40px',
                },
                '& .MuiTypography-root': {
                  width: '114px',
                  fontWeight: 400,
                  fontSize: '12px',
                  color: '#6B6F80',
                },
              }}
            >
              {/* Field Name & Type */}
              <Box>
                <Typography>Field Name & Type</Typography>
                <TextField
                  placeholder="name"
                  value={newFormField.name}
                  onChange={e =>
                    setNewFormField({ ...newFormField, name: e.target.value })
                  }
                  sx={{
                    width: '357px',
                    height: '40px',
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      borderRadius: '6px',
                      backgroundColor: '#FFFFFF',
                      '& fieldset': {
                        borderWidth: '1px',
                        borderColor: '#DCE0E7',
                        '&:hover': {
                          borderColor: '#DCE0E7',
                        },
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      padding: '8px 12px',
                      fontSize: '14px',
                      color: '#302B3F',
                      backgroundColor: '#FFFFFF',
                      '&::placeholder': {
                        color: '#6B6F80',
                      },
                    },
                  }}
                />
                <Select
                  value={newFormField.type}
                  onChange={e => {
                    const newType = e.target.value as FormFieldType;
                    const needsOptions =
                      newType === 'select' ||
                      newType === 'checkbox' ||
                      newType === 'radio';

                    setNewFormField({
                      ...newFormField,
                      type: newType,
                      options: needsOptions ? [''] : undefined,
                    });
                  }}
                  sx={{
                    width: '100px',
                    height: '40px',
                    border: '1px solid #DCE0E7',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    boxShadow: 'none',
                    backgroundColor: '#FFFFFF',
                    '.MuiOutlinedInput-notchedOutline': { border: 'none' },
                    '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
                      {
                        border: 'none',
                      },
                    '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                      {
                        border: 'none',
                      },
                    '& .MuiSelect-select': {
                      display: 'flex',
                      padding: '8px 12px',
                      paddingRight: '26px',
                      alignItems: 'center',
                      background: '#F8F9FC',
                      fontSize: '14px',
                      fontWeight: 400,
                      lineHeight: 'normal',
                      letterSpacing: '-0.14px',
                      color: theme.palette.lightGray.text,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                  }}
                >
                  <MenuItem value="text">text</MenuItem>
                  <MenuItem value="number">number</MenuItem>
                  <MenuItem value="email">email</MenuItem>
                  <MenuItem value="date">date</MenuItem>
                  <MenuItem value="password">password</MenuItem>
                  <MenuItem value="select">select</MenuItem>
                  <MenuItem value="checkbox">checkbox</MenuItem>
                  <MenuItem value="radio">radio</MenuItem>
                </Select>
              </Box>

              {/* Field Label */}
              <Box>
                <Typography>Field Label</Typography>
                <TextField
                  placeholder="Full Name"
                  value={newFormField.label}
                  onChange={e =>
                    setNewFormField({ ...newFormField, label: e.target.value })
                  }
                  fullWidth
                  sx={{
                    width: '459px',
                    height: '40px',
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      borderRadius: '6px',
                      backgroundColor: '#FFFFFF',
                      '& fieldset': {
                        borderWidth: '1px',
                        borderColor: '#DCE0E7',
                        '&:hover': {
                          borderColor: '#DCE0E7',
                        },
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      padding: '8px 12px',
                      fontSize: '14px',
                      color: '#302B3F',
                      backgroundColor: '#FFFFFF',
                      '&::placeholder': {
                        color: '#6B6F80',
                      },
                    },
                  }}
                />
              </Box>

              {/* Placeholder */}
              <Box>
                <Typography>Placeholder</Typography>
                <TextField
                  placeholder="Enter your full name"
                  value={newFormField.placeholder}
                  onChange={e =>
                    setNewFormField({
                      ...newFormField,
                      placeholder: e.target.value,
                    })
                  }
                  fullWidth
                  sx={{
                    width: '459px',
                    height: '40px',
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      borderRadius: '6px',
                      backgroundColor: '#FFFFFF',
                      '& fieldset': {
                        borderWidth: '1px',
                        borderColor: '#DCE0E7',
                        '&:hover': {
                          borderColor: '#DCE0E7',
                        },
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      padding: '8px 12px',
                      fontSize: '14px',
                      color: '#302B3F',
                      backgroundColor: '#FFFFFF',
                      '&::placeholder': {
                        color: '#6B6F80',
                      },
                    },
                  }}
                />
              </Box>

              {/* Options for select, checkbox, and radio types */}
              {(newFormField.type === 'select' ||
                newFormField.type === 'checkbox' ||
                newFormField.type === 'radio') && (
                <>
                  {(newFormField.options || []).map((option, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        height: '40px',
                      }}
                    >
                      <Typography
                        sx={{
                          width: '114px',
                          fontWeight: 400,
                          fontSize: '12px',
                          color: '#6B6F80',
                        }}
                      >
                        Option {index + 1}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          width: '459px',
                        }}
                      >
                        <TextField
                          value={option}
                          onChange={e => {
                            const updatedOptions = [
                              ...(newFormField.options || []),
                            ];
                            updatedOptions[index] = e.target.value;
                            setNewFormField({
                              ...newFormField,
                              options: updatedOptions,
                            });
                          }}
                          placeholder={`Enter option ${index + 1}`}
                          sx={{
                            flex: 1,
                            height: '40px',
                            '& .MuiOutlinedInput-root': {
                              height: '40px',
                              borderRadius: '6px',
                              backgroundColor: '#FFFFFF',
                              '& fieldset': {
                                borderWidth: '1px',
                                borderColor: '#DCE0E7',
                                '&:hover': {
                                  borderColor: '#DCE0E7',
                                },
                              },
                            },
                            '& .MuiOutlinedInput-input': {
                              padding: '8px 12px',
                              fontSize: '14px',
                              color: '#302B3F',
                              backgroundColor: '#FFFFFF',
                              '&::placeholder': {
                                color: '#6B6F80',
                              },
                            },
                          }}
                        />
                        {index === (newFormField.options?.length || 0) - 1 && (
                          <IconButton
                            onClick={() => {
                              setNewFormField({
                                ...newFormField,
                                options: [...(newFormField.options || []), ''],
                              });
                            }}
                            sx={{ padding: '4px' }}
                          >
                            <Plus size={16} />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  ))}
                </>
              )}

              {/* Required checkbox */}
              <Box>
                <Typography>Required</Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newFormField.required}
                      onChange={e =>
                        setNewFormField({
                          ...newFormField,
                          required: e.target.checked,
                        })
                      }
                      sx={{
                        marginLeft: '8px',
                        width: '20px',
                        height: '20px',
                        color: 'transparent',
                        '&.Mui-checked': {
                          color: '#6366F1',
                        },
                        '& .MuiSvgIcon-root': {
                          borderRadius: '6px',
                          border: '1px solid #DCE0E7',
                          backgroundColor: '#FFFFFF',
                          width: '20px',
                          height: '20px',
                          '&:hover': {
                            borderColor: '#6366F1',
                          },
                        },
                      }}
                    />
                  }
                  label=""
                  sx={{
                    marginLeft: '-11px',
                    '& .MuiFormControlLabel-label': {
                      display: 'none',
                    },
                  }}
                />
              </Box>

              {/* Cancel & Save Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 2,
                  marginTop: 1,
                  height: 'auto !important', // Override the previous height
                }}
              >
                <Button
                  onClick={handleCancelFormField}
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                    textTransform: 'none',
                    color: '#6B6F80',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: '#302B3F',
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveFormField}
                  disabled={!newFormField.name}
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                    textTransform: 'none',
                    boxShadow: 'none',
                    backgroundColor: '#6366F1',
                    borderRadius: '6px',
                    '&:hover': {
                      backgroundColor: '#4F46E5',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#E4E7EC',
                      color: '#9DA4AE',
                    },
                  }}
                >
                  {isEditingFormField ? 'Update Form Field' : 'Save Form Field'}
                </Button>
              </Box>
            </Box>
          </Box>
        ) : formFields.length > 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              backgroundColor: '#F8F8F8',
              padding: '12px 16px',
              borderRadius: '6px',
              border: '1px solid #DCE0E7',
            }}
          >
            {formFields.map((field: FormField, index: number) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  height: '40px',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 16px',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '8px',
                  border: '1px solid #DCE0E7',
                }}
              >
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: '12px',
                      color: '#6B6F80',
                    }}
                  >
                    {field.type}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: '14px',
                        color: '#302B3F',
                      }}
                    >
                      {field.label}
                    </Typography>
                    {field.required && (
                      <Tooltip title="Required">
                        <Typography
                          sx={{
                            color: '#EF4444',
                            fontSize: '14px',
                            marginLeft: '2px',
                          }}
                        >
                          *
                        </Typography>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton onClick={() => handleEditFormField(index)}>
                    <PencilSimple size={16} />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteFormField(index)}>
                    <TrashSimple size={16} weight="fill" color="#E74C3C" />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                backgroundColor: '#F8F8F8',
                borderRadius: '6px',
                padding: '16px',
                width: '100%',
                textAlign: 'center',
                color: '#6B6F80',
                fontSize: '12px',
                fontWeight: 400,
              }}
            >
              No fields added. Click "Add Field" to add your first form field or
              add parameters above to generate fields automatically
            </Box>
          </Box>
        )}
      </Box>

      <DeleteModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onDelete={
          deleteModalType === 'parameter'
            ? confirmDeleteParameter
            : confirmDeleteFormField
        }
        isPending={
          deleteModalType === 'parameter'
            ? isDeletingParam
            : isDeletingFormField
        }
        title={
          deleteModalType === 'parameter'
            ? 'Remove Parameter'
            : 'Remove Form Field'
        }
        description={
          deleteModalType === 'parameter'
            ? 'Are you sure you want to remove this parameter? This will also remove the corresponding form field.'
            : 'Are you sure you want to remove this form field? This action cannot be undone.'
        }
        confirmButtonText={
          deleteModalType === 'parameter' ? 'Remove Parameter' : 'Remove Field'
        }
        cancelButtonText="Cancel"
      />
    </Box>
  );
};

export default WebFormBuilder;
